import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Snackbar,
  Stack,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import QRCode from "react-qr-code";
import type { GuestWebConfig, PremiseConfig } from "@shared/types/foyer";
import {
  foyerApi,
  type GuestAnalyticsSummary,
  type GuestEmbedUrls,
  type GuestTokensResponse,
} from "../http/foyer";
import { PageHeader } from "../components/PageHeader";
import { SegmentedChoice } from "../components/startnav/StartNavLayout";
import { tokens } from "../theme";

const DEFAULT_CONFIG: GuestWebConfig = {
  enabled: false,
  wifi: { visible: false },
};

/** Rooms shown before "Show more rooms". */
const PAGE = 10;

type Toast = { msg: string; severity: "success" | "error" | "info" };
type SortKey = "room" | "status" | "created";

/**
 * Guest web (NendaPortal_1 "Gästwebb" 1800:26866): the phone companion a guest
 * reaches from a room QR code or the hotel's Wi-Fi page.
 *
 * The config is edited on the premise draft, so the header's Save persists it.
 * The embed key and the room codes are server actions and take effect at once,
 * as they always have.
 */
export default function GuestCompanionPage(props: {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}) {
  const { premise, onChange } = props;
  const premiseId = premise.premiseId;

  const config: GuestWebConfig = { ...DEFAULT_CONFIG, ...premise.guestWeb };
  const setConfig = (next: GuestWebConfig) =>
    onChange({ ...premise, guestWeb: next });

  const [tokensRes, setTokens] = useState<GuestTokensResponse | null>(null);
  const [analytics, setAnalytics] = useState<GuestAnalyticsSummary | null>(
    null
  );
  const [embed, setEmbed] = useState<GuestEmbedUrls | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({
    key: "room",
    asc: true,
  });
  const [shown, setShown] = useState(PAGE);
  const [managing, setManaging] = useState<string | null>(null);

  const reload = async () => {
    const [tk, an, gw] = await Promise.all([
      foyerApi.listGuestTokens(premiseId),
      foyerApi.guestAnalytics(premiseId, 30).catch(() => null),
      // Server-built: the embed URLs need the guest surface's hostname.
      foyerApi.getGuestWeb(premiseId).catch(() => null),
    ]);
    setTokens(tk);
    setAnalytics(an);
    setEmbed(gw?.embed ?? null);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch(() => setToast({ msg: "Failed to load", severity: "error" }))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId]);

  const run = async (
    fn: () => Promise<void>,
    ok: string | null,
    fail: string
  ) => {
    setBusy(true);
    try {
      await fn();
      if (ok) setToast({ msg: ok, severity: "success" });
    } catch {
      setToast({ msg: fail, severity: "error" });
    } finally {
      setBusy(false);
    }
  };

  const generateAll = (regenerate: boolean) => {
    if (
      regenerate &&
      !window.confirm(
        "Generate new codes for EVERY room? The QR codes already printed in the rooms stop working."
      )
    )
      return;
    return run(
      async () => {
        const res = await foyerApi.bulkIssueGuestTokens(premiseId, {
          regenerate,
        });
        await reload();
        setToast({
          msg: regenerate
            ? `Generated ${res.count} new codes`
            : `Created ${res.count} missing codes`,
          severity: "success",
        });
      },
      null,
      "Generation failed"
    );
  };

  const mintEmbedKey = (rotating: boolean) => {
    if (
      rotating &&
      !window.confirm(
        "Rotating replaces the Wi-Fi page link. The hotel's Wi-Fi page shows a broken image until they paste the new URL. Continue?"
      )
    )
      return;
    return run(
      async () => setEmbed((await foyerApi.mintGuestEmbedKey(premiseId)).embed),
      rotating ? "Link rotated" : "Link created",
      "Could not create the link"
    );
  };

  const removeEmbedKey = () => {
    if (
      !window.confirm(
        "Remove the Wi-Fi page link? The hotel's Wi-Fi page stops showing a code."
      )
    )
      return;
    return run(
      async () => {
        await foyerApi.deleteGuestEmbedKey(premiseId);
        setEmbed(null);
      },
      "Link removed",
      "Could not remove the link"
    );
  };

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setToast({ msg: `${what} copied`, severity: "success" });
    } catch {
      setToast({ msg: "Copy failed", severity: "error" });
    }
  };

  const rooms = useMemo(() => tokensRes?.rooms ?? [], [tokensRes]);
  const activeByRoom = useMemo(
    () => tokensRes?.activeByRoom ?? {},
    [tokensRes]
  );
  const activeCount = rooms.filter((r) => activeByRoom[r.id]).length;

  const visibleRooms = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? rooms.filter((r) => r.label.toLowerCase().includes(q))
      : rooms;
    const dir = sort.asc ? 1 : -1;
    const created = (id: string) => activeByRoom[id]?.createdAt ?? "";
    return [...filtered].sort((a, b) => {
      if (sort.key === "status") {
        const d = Number(!!activeByRoom[b.id]) - Number(!!activeByRoom[a.id]);
        if (d) return d * dir;
      } else if (sort.key === "created") {
        const d = created(a.id).localeCompare(created(b.id));
        if (d) return d * dir;
      }
      return a.label.localeCompare(b.label, undefined, { numeric: true }) * dir;
    });
  }, [rooms, activeByRoom, query, sort]);

  if (loading) return <LinearProgress />;

  const sortBy = (key: SortKey) =>
    setSort((s) => ({ key, asc: s.key === key ? !s.asc : true }));

  return (
    <Box sx={{ pb: 6, maxWidth: 1120 }} data-testid="guest-web-page">
      <PageHeader
        title="Guest web"
        subtitle="Give guests access to the hotel's services from their phone."
      />

      <SectionTitle
        title="Activation and Wi-Fi"
        subtitle="Turn on the guest web and choose how guests connect."
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          columnGap: 6,
          rowGap: 3,
          mb: 5,
        }}
      >
        <ChoiceRow
          title="Enable guest web"
          help="Off by default. Room QR codes only work once this is on."
          value={config.enabled}
          onChange={(enabled) => setConfig({ ...config, enabled })}
          testId="guest-enable"
        />
        <ChoiceRow
          title="Show Wi-Fi on the guest web"
          help="Shows the Wi-Fi details on the guest web."
          value={!!config.wifi?.visible}
          onChange={(visible) =>
            setConfig({ ...config, wifi: { ...config.wifi, visible } })
          }
          testId="guest-wifi-visible"
        />
      </Box>

      {/* The hotel's "you're connected" page: an image of the QR and a link. */}
      <SubTitle
        title="Wi-Fi page"
        subtitle="The link guests land on from the hotel's Wi-Fi page. It renders as a QR image and as a link that opens the guest web straight away."
      />
      {!embed ? (
        <Button
          variant="outlined"
          startIcon={<QrCode2Icon />}
          disabled={busy}
          onClick={() => mintEmbedKey(false)}
          sx={{ mb: 5, textTransform: "none" }}
          data-testid="guest-embed-create"
        >
          Create Wi-Fi page link
        </Button>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            sx={{ mb: 4 }}
          >
            {/* A live fetch of the real endpoint: if this renders, the hotel's
                page will render too. */}
            <Box
              component="img"
              src={`${embed.imageUrl}?size=320`}
              alt="Guest web QR code"
              sx={{ width: 104, height: 104, flexShrink: 0, bgcolor: "#fff" }}
            />
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <UrlRow
                label="Image URL (shown as a QR code, <img src>)"
                value={embed.imageUrl}
                onCopy={() => copy(embed.imageUrl, "Image URL")}
              />
              <UrlRow
                label="Link URL (opens embedded, <a href>)"
                value={embed.linkUrl}
                underline
                onCopy={() => copy(embed.linkUrl, "Link URL")}
              />
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems={{ md: "center" }}
            sx={{ mb: 5 }}
          >
            <Box sx={{ maxWidth: 460 }}>
              <ChoiceRow
                title="Allow room-specific codes"
                help="Lets the link open the guest web for one room (?room=101). Only for Wi-Fi portals that know the room - anyone who can read the page can also ask for a neighbouring room."
                value={!!config.embedRoomQr}
                onChange={(embedRoomQr) =>
                  setConfig({ ...config, embedRoomQr })
                }
                testId="guest-embed-room"
              />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={busy}
                onClick={() => mintEmbedKey(true)}
                sx={{ minWidth: 140, textTransform: "none" }}
                data-testid="guest-embed-rotate"
              >
                Rotate
              </Button>
              <Tooltip title="Remove the Wi-Fi page link">
                <IconButton
                  aria-label="Remove the Wi-Fi page link"
                  disabled={busy}
                  onClick={removeEmbedKey}
                  data-testid="guest-embed-remove"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </>
      )}

      <SubTitle
        title="QR codes per room"
        subtitle={`Printed in the rooms. ${activeCount} of ${rooms.length} rooms have an active QR code.`}
      />
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder="Search rooms…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShown(PAGE);
          }}
          sx={{ flex: 1, maxWidth: { md: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          data-testid="guest-room-search"
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={busy}
          onClick={() => generateAll(false)}
          sx={{ textTransform: "none" }}
          data-testid="guest-generate-missing"
        >
          Generate missing
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={busy}
          onClick={() => generateAll(true)}
          sx={{ textTransform: "none" }}
          data-testid="guest-generate-all"
        >
          Generate all
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          component="a"
          href={foyerApi.guestQrPdfUrl(premiseId)}
          target="_blank"
          rel="noreferrer"
          sx={{ textTransform: "none" }}
        >
          Print PDF
        </Button>
      </Stack>

      <Box
        sx={{
          border: "1px solid",
          borderColor: tokens.divider,
          borderRadius: "12px",
          bgcolor: tokens.surface,
          overflow: "hidden",
        }}
        data-testid="guest-room-table"
      >
        <Box
          sx={{
            ...ROW_GRID,
            minHeight: 48,
            borderBottom: "1px solid",
            borderColor: tokens.divider,
            fontSize: 13,
            color: tokens.inkMuted,
          }}
        >
          <HeaderCell label="Room" k="room" sort={sort} onSort={sortBy} />
          <HeaderCell label="Status" k="status" sort={sort} onSort={sortBy} />
          <HeaderCell label="Created" k="created" sort={sort} onSort={sortBy} />
          <Box>QR code</Box>
        </Box>
        {visibleRooms.length === 0 && (
          <Typography sx={{ p: 2, fontSize: 14, color: tokens.inkMuted }}>
            {rooms.length === 0
              ? "This place has no rooms yet."
              : "No room matches the search."}
          </Typography>
        )}
        {visibleRooms.slice(0, shown).map((room) => {
          const active = activeByRoom[room.id];
          return (
            <Box
              key={room.id}
              sx={{
                ...ROW_GRID,
                minHeight: 48,
                borderBottom: "1px solid",
                borderColor: tokens.divider,
                fontSize: 14,
              }}
              data-testid={`guest-room-${room.id}`}
            >
              <Box sx={{ fontWeight: 600 }}>{room.label}</Box>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: active ? tokens.success : tokens.warning,
                  }}
                />
                <Box
                  sx={{
                    fontWeight: 600,
                    color: active ? tokens.success : tokens.warning,
                  }}
                >
                  {active ? "Active" : "Missing"}
                </Box>
              </Stack>
              <Box>{active ? formatDate(active.createdAt) : "–"}</Box>
              <Box>
                {active ? (
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon fontSize="small" />}
                    onClick={() => setManaging(room.id)}
                    sx={{ textTransform: "none", px: 0 }}
                    data-testid={`guest-room-manage-${room.id}`}
                  >
                    Manage QR code
                  </Button>
                ) : (
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon fontSize="small" />}
                    disabled={busy}
                    onClick={() =>
                      run(
                        async () => {
                          await foyerApi.issueRoomToken(premiseId, room.id);
                          await reload();
                        },
                        `Created a code for ${room.label}`,
                        "Could not create the code"
                      )
                    }
                    sx={{ textTransform: "none", px: 0, fontWeight: 600 }}
                    data-testid={`guest-room-create-${room.id}`}
                  >
                    Create QR code
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
        {visibleRooms.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, fontSize: 13 }}
          >
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              Showing{" "}
              <Box component="strong" sx={{ color: tokens.ink }}>
                {Math.min(shown, visibleRooms.length)}
              </Box>{" "}
              of{" "}
              <Box component="strong" sx={{ color: tokens.ink }}>
                {visibleRooms.length}
              </Box>{" "}
              rooms
            </Typography>
            {shown < visibleRooms.length && (
              <Button
                size="small"
                endIcon={<ExpandMoreIcon />}
                onClick={() => setShown((n) => n + PAGE)}
                sx={{ textTransform: "none", color: tokens.ink }}
                data-testid="guest-room-more"
              >
                Show more rooms
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {analytics && <Usage analytics={analytics} />}

      {managing && (
        <ManageRoomDialog
          premiseId={premiseId}
          room={rooms.find((r) => r.id === managing)!}
          tokenId={activeByRoom[managing]?.id}
          busy={busy}
          onClose={() => setManaging(null)}
          onChanged={reload}
          onToast={setToast}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

const ROW_GRID = {
  display: "grid",
  gridTemplateColumns: "1.2fr 1fr 1.2fr 1fr",
  alignItems: "center",
  gap: 2,
  px: 2,
} as const;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
}

function SectionTitle({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 14, color: tokens.inkMuted }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

function SubTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography sx={{ mt: 0.25, fontSize: 13, color: tokens.inkMuted }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

/** A setting with its explanation on the left and Yes / No on the right. */
function ChoiceRow({
  title,
  help,
  value,
  onChange,
  testId,
}: {
  title: string;
  help: string;
  value: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <Stack direction="row" spacing={3} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ mt: 0.25, fontSize: 13, color: tokens.inkMuted }}>
          {help}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <SegmentedChoice
          value={value ? "yes" : "no"}
          options={[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ]}
          onChange={(v) => onChange(v === "yes")}
          testId={testId}
        />
      </Box>
    </Stack>
  );
}

/** One copyable URL in a tinted strip. */
function UrlRow({
  label,
  value,
  underline,
  onCopy,
}: {
  label: string;
  value: string;
  underline?: boolean;
  onCopy: () => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ px: 1.5, py: 1, bgcolor: tokens.rowAlt, borderRadius: "6px" }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: underline ? "underline" : "none",
          }}
          title={value}
        >
          {value}
        </Typography>
      </Box>
      <Button
        startIcon={<ContentCopyIcon />}
        onClick={onCopy}
        sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
      >
        Copy
      </Button>
    </Stack>
  );
}

function HeaderCell({
  label,
  k,
  sort,
  onSort,
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; asc: boolean };
  onSort: (k: SortKey) => void;
}) {
  return (
    <Box>
      <TableSortLabel
        active={sort.key === k}
        direction={sort.key === k && !sort.asc ? "desc" : "asc"}
        onClick={() => onSort(k)}
      >
        {label}
      </TableSortLabel>
    </Box>
  );
}

/** A room's code: shown as a QR, opened, replaced, or revoked. */
function ManageRoomDialog({
  premiseId,
  room,
  tokenId,
  busy,
  onClose,
  onChanged,
  onToast,
}: {
  premiseId: string;
  room: { id: string; label: string };
  tokenId?: string;
  busy: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onToast: (t: Toast) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let alive = true;
    foyerApi
      .revealRoomToken(premiseId, room.id)
      .then((res) => alive && setUrl(res.url))
      .catch(() => alive && setUrl(null));
    return () => {
      alive = false;
    };
  }, [premiseId, room.id]);

  const regenerate = async () => {
    if (
      !window.confirm(
        `Replace the code for ${room.label}? The QR code printed in the room stops working.`
      )
    )
      return;
    setWorking(true);
    try {
      const issued = await foyerApi.issueRoomToken(premiseId, room.id);
      setUrl(issued.url);
      await onChanged();
      onToast({ msg: `New code for ${room.label}`, severity: "success" });
    } catch {
      onToast({ msg: "Could not replace the code", severity: "error" });
    } finally {
      setWorking(false);
    }
  };

  const revoke = async () => {
    if (!tokenId) return;
    setWorking(true);
    try {
      await foyerApi.revokeGuestToken(premiseId, tokenId);
      await onChanged();
      onToast({ msg: `Revoked the code for ${room.label}`, severity: "info" });
      onClose();
    } catch {
      onToast({ msg: "Revoke failed", severity: "error" });
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>QR code · {room.label}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            minHeight: 200,
            alignItems: "center",
          }}
        >
          {url ? (
            <Box sx={{ bgcolor: "#fff", p: 1.5 }}>
              <QRCode value={url} size={180} />
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              Loading the code…
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Stack direction="row" spacing={1}>
          {url && (
            <Button
              startIcon={<OpenInNewIcon />}
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              Open
            </Button>
          )}
          <Button
            startIcon={<RefreshIcon />}
            disabled={busy || working}
            onClick={regenerate}
          >
            New code
          </Button>
          <Button
            color="error"
            startIcon={<BlockIcon />}
            disabled={busy || working || !tokenId}
            onClick={revoke}
          >
            Revoke
          </Button>
        </Stack>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Scans and views over the last 30 days - kept from the old page. */
function Usage({ analytics }: { analytics: GuestAnalyticsSummary }) {
  const top = (data: Record<string, number>) =>
    Object.entries(data)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  return (
    <Box sx={{ mt: 5 }} data-testid="guest-usage">
      <SubTitle
        title="Usage"
        subtitle="Scans and page views over the last 30 days."
      />
      <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
        <Metric label="Scans" value={analytics.totals.resolves} />
        <Metric label="Page views" value={analytics.totals.views} />
        <Metric
          label="Rooms scanned"
          value={Object.keys(analytics.byRoom).length}
        />
        {[
          ["Top sections", analytics.sections],
          ["Languages", analytics.languages],
        ].map(([title, data]) => (
          <Box key={title as string} sx={{ minWidth: 160 }}>
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
              {title as string}
            </Typography>
            {top(data as Record<string, number>).length === 0 ? (
              <Typography sx={{ fontSize: 13 }}>No data yet</Typography>
            ) : (
              top(data as Record<string, number>).map(([k, v]) => (
                <Stack key={k} direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 13 }}>{k}</Typography>
                  <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                    {v}
                  </Typography>
                </Stack>
              ))
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 24, fontWeight: 600 }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
        {label}
      </Typography>
    </Box>
  );
}
